// ============================================================================
// FarmerSignaturesTab — producer signing surface (Build S3, Task #19).
// ============================================================================
// Route: #my/signatures. Flow per contract §5.3:
//   list → detail (inline PDF preview) → CONSENT SCREEN (the four-element
//   §7001(c)(1) language rendered VERBATIM) → SignatureCapture → confirmation.
// The consent language comes FROM THE API (single source of truth in
// esign.ts) so the UI can never drift from the recorded language_version.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, PenLine, ArrowLeft, CheckCircle2, Clock } from 'lucide-react';
import { api, type FarmerEnvelope, type FarmerEnvelopeDetail, type SignInput } from '../api';
import { SignatureCapture } from './SignatureCapture';
import { useFarmerAuth } from '../farmerAuth';

export function FarmerSignaturesTab() {
  const { token: authToken } = useFarmerAuth();
  const token = authToken ?? '';
  const [envelopes, setEnvelopes] = useState<FarmerEnvelope[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FarmerEnvelopeDetail | null>(null);
  const [phase, setPhase] = useState<'list' | 'review' | 'consent' | 'sign' | 'done'>('list');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try { setEnvelopes((await api.farmerEnvelopes(token)).envelopes); }
    catch (e: any) { setError(e?.message || 'Could not load signature requests'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Signed-out direct navigation lands on sign-in, not a blank page —
  // FarmerHomePage precedent (Copilot r1).
  useEffect(() => {
    if (!token) window.location.hash = '#farmer-signin';
  }, [token]);

  async function open(envelopeId: string) {
    setBusy(true); setError('');
    try {
      const d = await api.farmerEnvelopeDetail(token, envelopeId);
      setDetail(d); setOpenId(envelopeId);
      setPhase(d.my_party.status === 'signed' ? 'done' : 'review');
    } catch (e: any) { setError(e?.message || 'Could not open the request'); }
    finally { setBusy(false); }
  }

  async function consent() {
    if (!openId) return;
    setBusy(true); setError('');
    try {
      await api.farmerEnvelopeConsent(token, openId);
      const d = await api.farmerEnvelopeDetail(token, openId);
      setDetail(d); setPhase('sign');
    } catch (e: any) { setError(e?.message || 'Could not record consent'); }
    finally { setBusy(false); }
  }

  async function sign(input: SignInput) {
    if (!openId) return;
    setBusy(true); setError('');
    try {
      await api.farmerEnvelopeSign(token, openId, input);
      setPhase('done');
      await refresh();
    } catch (e: any) { setError(e?.message || 'Could not record signature'); }
    finally { setBusy(false); }
  }

  async function decline() {
    if (!openId || declineReason.trim().length < 2) return;
    setBusy(true); setError('');
    try {
      await api.farmerEnvelopeDecline(token, openId, declineReason.trim());
      setDeclineOpen(false); setOpenId(null); setDetail(null); setPhase('list');
      await refresh();
    } catch (e: any) { setError(e?.message || 'Could not record decline'); }
    finally { setBusy(false); }
  }

  if (!token) return null;

  // ---------- detail phases ----------
  if (openId && detail) {
    const d = detail;
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <button type="button" onClick={() => { setOpenId(null); setDetail(null); setPhase('list'); }}
          className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
          <ArrowLeft className="h-4 w-4" /> All signature requests
        </button>
        <h1 className="text-xl font-bold text-uif-green-dark">{d.envelope.title}</h1>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />{error}
          </div>
        )}

        {phase === 'done' ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center space-y-2">
            <CheckCircle2 className="h-10 w-10 text-uif-green mx-auto" />
            <div className="font-semibold text-uif-green-dark">Your signature is recorded.</div>
            <p className="text-sm text-gray-600">
              When all parties have signed, the executed copy will appear under Documents and you will
              receive an email confirmation.
            </p>
          </div>
        ) : !d.my_party.my_turn ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex items-start gap-2">
            <Clock className="h-4 w-4 mt-0.5" />
            Waiting on an earlier signer — you will be notified when it is your turn.
          </div>
        ) : (
          <>
            {d.envelope.signing_instructions && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
                {d.envelope.signing_instructions}
              </div>
            )}
            {d.preview_url && (
              <iframe title="Document preview" src={d.preview_url}
                className="w-full h-[480px] border border-gray-200 rounded-lg bg-white" />
            )}

            {phase === 'review' && (
              <div className="flex gap-2">
                <button type="button" onClick={() => setPhase('consent')}
                  className="flex-1 rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 hover:bg-uif-green-dark">
                  Continue to consent
                </button>
                <button type="button" onClick={() => setDeclineOpen(true)}
                  className="rounded-md border border-gray-300 text-gray-700 px-4 py-2.5 hover:border-red-400 hover:text-red-600">
                  Decline
                </button>
              </div>
            )}

            {phase === 'consent' && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                  {d.consent_language}
                </pre>
                <div className="flex gap-2">
                  <button type="button" disabled={busy} onClick={() => void consent()}
                    className="flex-1 rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 disabled:opacity-40 hover:bg-uif-green-dark">
                    {busy ? 'Recording…' : 'I agree and consent'}
                  </button>
                  <button type="button"
                    onClick={() => {
                      // §0.6 item-6: leave the audit trace FIRST, then open
                      // mail. Best-effort — a failed trace must not block the
                      // signer's working paper channel.
                      if (openId) void api.farmerEnvelopePaperPreference(token, openId).catch(() => undefined);
                      window.location.href = 'mailto:support@upinfarms.com?subject=Prefer%20paper%20signing';
                    }}
                    className="rounded-md border border-gray-300 text-gray-700 px-4 py-2.5 hover:border-uif-green">
                    Prefer paper — contact us
                  </button>
                </div>
              </div>
            )}

            {phase === 'sign' && d.my_party.consent_recorded && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <SignatureCapture
                  displayName={d.my_party.display_name}
                  busy={busy}
                  onSubmit={(input) => void sign(input)}
                />
              </div>
            )}
          </>
        )}

        {declineOpen && (
          <div className="bg-white border border-red-200 rounded-lg p-4 space-y-2">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Reason for declining *</span>
              <textarea rows={2} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <div className="flex gap-2">
              <button type="button" disabled={busy || declineReason.trim().length < 2}
                onClick={() => void decline()}
                className="rounded-md bg-red-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
                Decline to sign
              </button>
              <button type="button" onClick={() => setDeclineOpen(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm">Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- list ----------
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <a href="#my" className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
        <ArrowLeft className="h-4 w-4" /> My account
      </a>
      <h1 className="text-2xl font-bold text-uif-green-dark flex items-center gap-2">
        <PenLine className="h-6 w-6" /> Signatures
      </h1>
      <p className="text-sm text-gray-500">Documents Up in Farms has asked you to sign.</p>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
      )}
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-uif-green" /></div>
      ) : envelopes.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
          Nothing needs your signature right now.
        </div>
      ) : (
        <div className="space-y-2">
          {envelopes.map((e) => (
            <button key={e.envelope_id} type="button" onClick={() => void open(e.envelope_id)}
              className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-uif-green/60 transition">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-gray-900">{e.title}</div>
                  <div className="text-xs text-gray-500">
                    {e.farm_code} · {e.subject_title ?? 'Document'}
                    {e.expires_at && e.envelope_status !== 'completed'
                      ? ` · expires ${new Date(e.expires_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <span className={
                  'text-xs font-semibold px-2 py-1 rounded-full ' +
                  (e.my_status === 'signed'
                    ? 'bg-green-100 text-green-800'
                    : e.envelope_status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-amber-100 text-amber-800')
                }>
                  {e.my_status === 'signed'
                    ? (e.envelope_status === 'completed' ? 'Completed' : 'You signed')
                    : 'Needs your signature'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
